import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    UploadCloud,
    FileText,
    X,
    CheckCircle2,
    Lock,
    Zap,
    Languages,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import UpgradeModal from "@/components/UpgradeModal";

const SUMMARY_LANGUAGES = [
    "Hindi",
    "English",
    "Tamil",
    "Telugu",
    "Marathi",
    "Bengali",
    "Gujarati",
    "Kannada",
    "Malayalam",
    "Punjabi",
    "Odia",
    "Urdu",
    "Assamese",
];

function getProgressLabel(progress) {
    if (progress < 20) return "Uploading PDF...";
    if (progress < 55) return "AI is reading the document...";
    if (progress < 85) return "Extracting eligibility & risks...";
    return "Finalizing your report...";
}

export default function UploadTender() {
    const { profile, refreshProfile } = useAuth();
    const [drag, setDrag] = useState(false);
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [analyzing, setAnalyzing] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [language, setLanguage] = useState("Hindi");
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const credits = profile?.credits ?? 0;

    const handleFiles = (files) => {
        if (!files?.length) return;
        const f = files[0];
        if (f.type !== "application/pdf") {
            toast.error("Only PDF files are supported.");
            return;
        }
        setFile(f);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        handleFiles(e.dataTransfer.files);
    };

    const startAnalysis = async () => {
        if (!file) return;
        if (credits <= 0) {
            setShowUpgrade(true);
            return;
        }

        setAnalyzing(true);
        setProgress(0);
        const interval = setInterval(() => {
            setProgress((p) => Math.min(94, p + (p < 25 ? 4 : 1)));
        }, 600);

        try {
            // 1. Run real AI analysis on the backend (no credit deducted if it fails)
            const {
                data: { session },
            } = await supabase.auth.getSession();
            const form = new FormData();
            form.append("file", file);
            form.append("language", language);
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/analyze`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${session?.access_token}` },
                    body: form,
                },
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "AI analysis failed. Please try again.");
            }
            const result = await res.json();

            // 2. Deduct 1 credit + create the analysis row
            const { data: analysisId, error } = await supabase.rpc(
                "start_tender_analysis",
                {
                    p_file_name: file.name,
                    p_credits_required: 1,
                },
            );
            clearInterval(interval);
            if (error) {
                if (error.message?.toLowerCase().includes("insufficient credits")) {
                    setShowUpgrade(true);
                } else {
                    toast.error(error.message || "Could not start analysis");
                }
                setAnalyzing(false);
                setProgress(0);
                return;
            }

            // 3. Attach the AI result to the analysis row
            const { error: updateError } = await supabase
                .from("tender_analyses")
                .update({ result })
                .eq("id", analysisId);
            if (updateError) {
                console.error("Failed to store analysis result:", updateError.message);
            }

            setProgress(100);
            await refreshProfile();
            setTimeout(() => {
                toast.success("Analysis complete!");
                navigate(`/dashboard/analysis/${analysisId}`);
            }, 400);
        } catch (err) {
            clearInterval(interval);
            toast.error(err.message || "Could not start analysis");
            setAnalyzing(false);
            setProgress(0);
        }
    };

    return (
        <div className="space-y-6" data-testid="upload-page">
            <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />

            <div className="flex items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                        Upload a Tender
                    </h1>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        Drop your tender PDF below. AI will analyze it in under 60 seconds.
                    </p>
                </div>
                <div className="hidden sm:block text-right">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                        Credits
                    </p>
                    <p className="text-2xl font-bold font-mono text-slate-900 dark:text-slate-50">
                        {credits}
                    </p>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    {!file ? (
                        <div
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDrag(true);
                            }}
                            onDragLeave={() => setDrag(false)}
                            onDrop={onDrop}
                            onClick={() => inputRef.current?.click()}
                            className={`relative rounded-2xl border-2 border-dashed cursor-pointer transition-all p-10 lg:p-14 text-center ${
                                drag
                                    ? "border-indigo-500 bg-indigo-50/80 dark:bg-indigo-950/30"
                                    : "border-slate-300 dark:border-slate-700 bg-indigo-50/30 dark:bg-indigo-950/10 hover:border-indigo-300 dark:hover:border-indigo-800"
                            }`}
                            data-testid="upload-dropzone"
                        >
                            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                                <UploadCloud className="h-7 w-7 text-white" />
                            </div>
                            <h3 className="mt-5 text-xl font-bold text-slate-900 dark:text-slate-50">
                                Drop your tender PDF here
                            </h3>
                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                or{" "}
                                <span className="text-indigo-600 dark:text-indigo-400 font-semibold underline">
                                    browse files
                                </span>{" "}
                                from your computer
                            </p>
                            <p className="mt-6 text-xs text-slate-500">
                                Supported: PDF • Max 50 MB • 1 credit per analysis
                            </p>
                            <input
                                ref={inputRef}
                                type="file"
                                accept="application/pdf"
                                className="hidden"
                                onChange={(e) => handleFiles(e.target.files)}
                                data-testid="upload-file-input"
                            />
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-950/40">
                                    <FileText className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-slate-900 dark:text-slate-50 truncate">
                                        {file.name}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB • PDF
                                    </p>
                                    {analyzing && (
                                        <div className="mt-4">
                                            <Progress value={progress} className="h-2" />
                                            <div className="flex items-center justify-between mt-2 text-xs">
                                                <span className="text-slate-600 dark:text-slate-300">
                                                    {getProgressLabel(progress)}
                                                </span>
                                                <span className="font-mono font-semibold text-slate-900 dark:text-slate-50">
                                                    {progress}%
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {!analyzing && (
                                    <button
                                        onClick={() => setFile(null)}
                                        className="text-slate-400 hover:text-slate-700"
                                        data-testid="upload-remove-file"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                )}
                            </div>
                            {!analyzing && (
                                <>
                                    <div className="mt-5">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                                            <Languages className="h-3.5 w-3.5" /> Summary
                                            Language
                                        </label>
                                        <Select value={language} onValueChange={setLanguage}>
                                            <SelectTrigger
                                                className="mt-2 w-full"
                                                data-testid="upload-language-select"
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SUMMARY_LANGUAGES.map((l) => (
                                                    <SelectItem key={l} value={l}>
                                                        {l}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="mt-1.5 text-xs text-slate-500">
                                            Your report&apos;s &ldquo;All Language
                                            Summary&rdquo; will be written in this language.
                                        </p>
                                    </div>
                                    <Button
                                        onClick={startAnalysis}
                                        className="mt-5 w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-500/25"
                                        data-testid="upload-analyze-btn"
                                    >
                                        <Zap className="h-4 w-4 mr-2" /> Start Analysis (1
                                        credit)
                                    </Button>
                                </>
                            )}
                        </div>
                    )}

                    <div className="mt-6 grid sm:grid-cols-3 gap-3">
                        {[
                            { icon: Lock, t: "Encrypted Upload", d: "TLS 1.3" },
                            { icon: Zap, t: "Under 60s", d: "Average time" },
                            { icon: Languages, t: "All languages", d: "Every plan" },
                        ].map((b) => (
                            <div
                                key={b.t}
                                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex items-center gap-3"
                            >
                                <b.icon className="h-4 w-4 text-indigo-600" />
                                <div>
                                    <p className="text-xs font-semibold text-slate-900 dark:text-slate-50">
                                        {b.t}
                                    </p>
                                    <p className="text-[11px] text-slate-500">{b.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 h-fit">
                    <h4 className="font-semibold text-slate-900 dark:text-slate-50">
                        What you&apos;ll get
                    </h4>
                    <p className="text-xs text-slate-500 mt-1">
                        Complete analysis in a single report
                    </p>
                    <ul className="mt-5 space-y-3">
                        {[
                            "AI-generated tender summary",
                            "Eligibility assessment",
                            "EMD detection & deadline",
                            "Required documents checklist",
                            "Risk factor highlighting",
                            "All Language Summary (multi-script)",
                            "PDF report download",
                            "AI bid readiness plan",
                        ].map((x) => (
                            <li
                                key={x}
                                className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300"
                            >
                                <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                {x}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-6 pt-5 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 leading-relaxed">
                        💡 <strong className="text-slate-700 dark:text-slate-300">Pro tip:</strong>{" "}
                        Upload the original tender PDF (not scanned images) for the most
                        accurate results.
                    </div>
                </div>
            </div>
        </div>
    );
}
